<script setup>
import { ref, computed } from 'vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import {
  CUSTOM_COLOR_SWATCHES,
  DISCO_COLOR,
} from 'dashboard/components-next/planning/Scheduler/allocationCategories';
import {
  readRecentAllocationColors,
  pushRecentAllocationColor,
} from 'dashboard/components-next/planning/Scheduler/recentAllocationColors';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const recentColors = ref(readRecentAllocationColors());

const customSwatches = computed(() => {
  const defaults = new Set(CUSTOM_COLOR_SWATCHES.map(c => c.toLowerCase()));
  return recentColors.value.filter(hex => !defaults.has(hex.toLowerCase()));
});

const isDiscoActive = computed(() => props.modelValue === DISCO_COLOR);

const selectSwatch = swatch => {
  if (props.modelValue === swatch) {
    emit('update:modelValue', '');
  } else {
    emit('update:modelValue', swatch);
    if (swatch !== DISCO_COLOR) {
      recentColors.value = pushRecentAllocationColor(swatch);
    }
  }
};

const toggleDisco = () => {
  emit('update:modelValue', isDiscoActive.value ? '' : DISCO_COLOR);
};

// ColorPicker only understands hex values; keep the disco sentinel out of it.
const pickerColor = computed({
  get: () => (isDiscoActive.value ? '' : props.modelValue || ''),
  set: value => {
    emit('update:modelValue', value || '');
    if (value && value !== DISCO_COLOR) {
      recentColors.value = pushRecentAllocationColor(value);
    }
  },
});

const resetColor = () => {
  emit('update:modelValue', '');
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2 flex-wrap">
      <button
        v-for="swatch in CUSTOM_COLOR_SWATCHES"
        :key="swatch"
        type="button"
        class="size-7 rounded-full border-2 transition-all"
        :class="
          modelValue === swatch
            ? 'border-n-slate-12 scale-110'
            : 'border-n-weak hover:border-n-slate-8'
        "
        :style="{ backgroundColor: swatch }"
        :title="swatch"
        @click="selectSwatch(swatch)"
      />
    </div>
    <div v-if="customSwatches.length" class="flex items-center gap-2 flex-wrap">
      <button
        v-for="swatch in customSwatches"
        :key="`custom-${swatch}`"
        type="button"
        class="size-7 rounded-full border-2 transition-all"
        :class="
          modelValue === swatch
            ? 'border-n-slate-12 scale-110'
            : 'border-n-weak hover:border-n-slate-8'
        "
        :style="{ backgroundColor: swatch }"
        :title="swatch"
        @click="selectSwatch(swatch)"
      />
    </div>
    <div class="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        class="disco-swatch size-7 rounded-full border-2 transition-all"
        :class="
          isDiscoActive
            ? 'border-n-slate-12 scale-110'
            : 'border-n-weak hover:border-n-slate-8'
        "
        :title="$t('PLANNING.ALLOCATIONS.COLOR.DISCO')"
        @click="toggleDisco"
      />
      <ColorPicker v-model="pickerColor" />
      <button
        v-if="modelValue"
        type="button"
        class="flex items-center justify-center size-8 rounded-full border border-n-weak hover:bg-n-slate-4 transition-colors"
        :title="$t('PLANNING.ALLOCATIONS.COLOR.RESET')"
        @click="resetColor"
      >
        <span class="i-lucide-rotate-ccw size-5 shrink-0 text-n-slate-11" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.disco-swatch {
  background-image: linear-gradient(
    90deg,
    #ff4d4d,
    #ff9a3c,
    #ffd93d,
    #6bcf63,
    #4dd0e1,
    #5c9bff,
    #a56bff,
    #ff5ce0,
    #ff4d4d
  );
  background-size: 300% 100%;
  animation: discoSwatchShift 4s linear infinite;
}

@keyframes discoSwatchShift {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 300% 50%;
  }
}
</style>
